import {
  Box,
  type BoxProps,
  Stack,
  Typography,
  alpha,
  styled,
  useTheme,
} from "@mui/material"
import {
  EvolutionStatChip,
  PillsIcon,
  type SalesUniverse,
  type SpecificUniverseName,
  formatPrice,
  labelOf,
  noDecimalsFormatOptions,
  specificUniverseEnum,
  universeEnum,
} from "@pharmupp/p3-front-commons"
import { forwardRef } from "react"
import { isDefined } from "remeda"
import type { SaleIcon, SalesIconReferential } from "../referential"

type SalesProps = {
  sales: SalesUniverse[]
  iconsReferential: SalesIconReferential
} & Omit<BoxProps, "children">

export const Sales = forwardRef<HTMLDivElement, SalesProps>(
  ({ sales, iconsReferential, ...props }, ref) => {
    return (
      <SalesContainer {...props} ref={ref}>
        {sales.map((sale) => (
          <Sale {...sale} key={sale.name} Icon={iconsReferential[sale.name]} />
        ))}
      </SalesContainer>
    )
  },
)

type SaleProps = SalesUniverse & {
  Icon?: SaleIcon
}

const Sale = ({ name, sales, uppSales, Icon = PillsIcon }: SaleProps) => {
  const { palette } = useTheme()
  const label = getLabel(name)

  return (
    <Stack spacing={1} width="100%">
      <Stack direction="row" justifyContent="space-between" px={4}>
        <Typography variant="body2" color="common.darkBlue">
          {label}
        </Typography>
        <Typography variant="caption" color="grey.ultraLight">
          Évol. PharmUPP
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        bgcolor={alpha(palette.primary.main, 0.04)}
        py={2}
        px={4}
        borderRadius="2000px"
        spacing={2}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Icon height={20} style={{ color: palette.grey.ultraLight }} />
          <Typography variant="h6" color="common.darkBlue">
            {isDefined(sales?.mainStat)
              ? formatPrice(sales?.mainStat, noDecimalsFormatOptions)
              : "-"}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <EvolutionStatChip evol={sales?.evolution} border />
          <EvolutionStatChip evol={uppSales?.evolution} border />
        </Stack>
      </Stack>
    </Stack>
  )
}

const isSpecificUniverseName = (
  name: SalesUniverse["name"],
): name is SpecificUniverseName => Object.keys(specificUniverseEnum).includes(name)

const getLabel = (universe: SalesUniverse["name"]) => {
  if (isSpecificUniverseName(universe)) {
    return labelOf(specificUniverseEnum, universe)
  }
  return labelOf(universeEnum, universe)
}

export const SalesContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: theme.spacing(4),
  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(2),
  },
}))
